"use client";

import {
	PropsWithChildren,
	createContext,
	useCallback,
	useContext,
	useMemo,
	useSyncExternalStore,
} from "react";
import { StepType } from "@/types/routes";
import { readStepCookie } from "@/utils/read-step-cookie";

type StepNavigationContextValue = {
	step: StepType;
	refreshStep: () => void;
};

const StepNavigationContext = createContext<StepNavigationContextValue | null>(
	null
);

const stepCookieListeners = new Set<() => void>();

const subscribeStepCookie = (onStoreChange: () => void) => {
	stepCookieListeners.add(onStoreChange);
	return () => {
		stepCookieListeners.delete(onStoreChange);
	};
};

const notifyStepCookie = () => {
	stepCookieListeners.forEach((listener) => listener());
};

export const useStepNavigation = () => {
	const context = useContext(StepNavigationContext);
	if (!context) {
		throw new Error(
			"useStepNavigation must be used within StepNavigationProvider"
		);
	}
	return context;
};

type Props = PropsWithChildren<{
	initialStep: StepType;
	ignoreCookie?: boolean;
}>;

export const StepNavigationProvider = ({
	initialStep,
	ignoreCookie = false,
	children,
}: Props) => {
	const getSnapshot = useCallback(
		() => (ignoreCookie ? initialStep : (readStepCookie() ?? initialStep)),
		[ignoreCookie, initialStep]
	);
	const getServerSnapshot = useCallback(() => initialStep, [initialStep]);

	const step = useSyncExternalStore(
		subscribeStepCookie,
		getSnapshot,
		getServerSnapshot
	);

	const refreshStep = useCallback(() => {
		notifyStepCookie();
	}, []);

	const value = useMemo(
		() => ({
			step,
			refreshStep,
		}),
		[step, refreshStep]
	);

	return (
		<StepNavigationContext.Provider value={value}>
			{children}
		</StepNavigationContext.Provider>
	);
};
